import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

type Status = 'loading' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token was provided in the URL.');
      return;
    }

    let cancelled = false;

    const verify = async () => {
      try {
        const resp = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = await resp.json();
        if (cancelled) return;

        if (resp.ok && data.verified) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully');
        } else {
          setStatus('error');
          setMessage(data.error || 'Email verification failed.');
        }
      } catch (err: any) {
        if (cancelled) return;
        setStatus('error');
        setMessage(err.message || 'A network error occurred during verification.');
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black uppercase tracking-widest text-navy-900 dark:text-cream-50 mb-2">
            Email Verification
          </h1>
        </div>

        <div className="glass-panel rounded-lg shadow-xl p-8 text-center">
          {status === 'loading' && (
            <div>
              <svg
                className="animate-spin h-12 w-12 text-gold-500 mx-auto mb-4"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-navy-500 dark:text-cream-300 font-bold uppercase tracking-widest text-sm">
                Verifying...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-navy-900 dark:text-cream-50 font-bold text-lg mb-6">
                {message}
              </p>
              <Link
                to="/login"
                className="inline-block bg-navy-700 hover:bg-navy-800 active:bg-navy-900 text-cream-50 px-6 py-3 rounded-lg font-black uppercase tracking-widest text-sm transition"
              >
                Continue to Login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <p className="text-navy-900 dark:text-cream-50 font-bold text-lg mb-6">
                {message}
              </p>
              <Link
                to="/"
                className="inline-block bg-navy-700 hover:bg-navy-800 active:bg-navy-900 text-cream-50 px-6 py-3 rounded-lg font-black uppercase tracking-widest text-sm transition"
              >
                Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
